import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetAppResponseBody } from '~/lib/api/modules/Apps';
import type { IUseUpdateAppMutationValues } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';
import type { IAppFormValues } from './AppForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppMutation } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';
import { AppForm } from './AppForm';

export type IUpdateAppFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiGetAppResponseBody,
    inputValues: IUseUpdateAppMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
};

export const UpdateAppForm: React.FC<IUpdateAppFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId } = props;
  const appQuery = useAppQuery({ appId });
  const updateAppMutation = useUpdateAppMutation();
  const { t } = useTranslation();
  const app = appQuery.data;

  const handleSubmit: SubmitHandler<IAppFormValues> = (values) => {
    if (updateAppMutation.isLoading) {
      return;
    }

    return updateAppMutation.mutateAsync(
      {
        appId,
        body: {
          name: values.name,
          iconId: values.icons?.[0]?.id,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>{t('UpdateAppForm.title')}</ContentBlock.Title>
      <ContentBlock.Suspense
        loading={appQuery.isLoading}
        error={appQuery.error}
      >
        {app && (
          <AppForm
            mode="edition"
            cancelUrl={cancelUrl}
            defaultValues={{
              name: app.name,
              icons: app.icon
                ? [
                    {
                      type: 'picture',
                      id: app.icon._id,
                    },
                  ]
                : [],
              themeColorPrimary: app.settings?.press?.env?.appThemeColorPrimary,
            }}
            onSubmit={handleSubmit}
            appId={appId}
          />
        )}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
